import { useState, useEffect } from 'react';

export interface SessionFormData {
  title: string;
  date: string;
  time: string;
  duration: string;
  maxStudents: number;
  level: string;
  board: string;
  topic: string;
  sessionType: string;
  platform: string;
  meetingLink: string;
  notes: string;
  status: string;
}

interface SessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SessionFormData) => void;
  initialData?: Partial<SessionFormData> & { id?: number };
  mode: 'create' | 'edit';
}

const defaultForm: SessionFormData = {
  title: '',
  date: '',
  time: '',
  duration: '60 min',
  maxStudents: 20,
  level: 'GCSE',
  board: 'AQA',
  topic: '',
  sessionType: 'Live Revision',
  platform: 'Zoom',
  meetingLink: '',
  notes: '',
  status: 'upcoming',
};

const DURATIONS = ['30 min', '45 min', '60 min', '90 min', '120 min'];
const LEVELS = ['GCSE', 'A-Level', 'Both'];
const BOARDS = ['AQA', 'OCR', 'Edexcel', 'WJEC', 'Cambridge'];
const SESSION_TYPES = ['Live Revision', 'Mock Walkthrough', 'Q&A Session', 'Programming Workshop', 'Exam Technique', 'Topic Deep Dive'];
const PLATFORMS = ['Zoom', 'Google Meet', 'Microsoft Teams', 'Discord', 'In-Person'];

export default function SessionModal({ isOpen, onClose, onSubmit, initialData, mode }: SessionModalProps) {
  const [form, setForm] = useState<SessionFormData>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof SessionFormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({ ...defaultForm, ...initialData });
      } else {
        setForm(defaultForm);
      }
      setErrors({});
      setSubmitted(false);
    }
  }, [isOpen, initialData]);

  const set = (key: keyof SessionFormData, value: string | number) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof SessionFormData, string>> = {};
    if (!form.title.trim()) newErrors.title = 'Session title is required';
    if (!form.date) newErrors.date = 'Date is required';
    if (!form.time) newErrors.time = 'Time is required';
    if (!form.topic.trim()) newErrors.topic = 'Topic is required';
    if (form.maxStudents < 1 || form.maxStudents > 200) newErrors.maxStudents = 'Must be between 1 and 200';
    if (form.meetingLink && !/^https?:\/\/.+/.test(form.meetingLink)) newErrors.meetingLink = 'Enter a valid URL (https://...)';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitted(true);
    setTimeout(() => {
      onSubmit(form);
      onClose();
    }, 800);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg">
              <i className={`${mode === 'create' ? 'ri-add-circle-line' : 'ri-edit-line'} text-emerald-700 text-lg`}></i>
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                {mode === 'create' ? 'Request New Session' : 'Edit Session'}
              </h2>
              <p className="text-xs text-slate-400">{mode === 'create' ? 'Fill in the details below to request a session' : 'Update the session details'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5">
          {/* Session Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Session Title <span className="text-red-400">*</span>
            </label>
            <input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. GCSE AQA Paper 1 Revision"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.title ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          {/* Session Type */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Session Type</label>
            <div className="flex flex-wrap gap-2">
              {SESSION_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => set('sessionType', type)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${form.sessionType === type ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Topic */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Topic / Focus Area <span className="text-red-400">*</span>
            </label>
            <input
              value={form.topic}
              onChange={(e) => set('topic', e.target.value)}
              placeholder="e.g. Binary, Networks, Algorithms, Python OOP"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.topic ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
            />
            {errors.topic && <p className="text-xs text-red-500 mt-1">{errors.topic}</p>}
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.date ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
              />
              {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Time <span className="text-red-400">*</span>
              </label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => set('time', e.target.value)}
                className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.time ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
              />
              {errors.time && <p className="text-xs text-red-500 mt-1">{errors.time}</p>}
            </div>
          </div>

          {/* Duration & Max Students */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Duration</label>
              <select
                value={form.duration}
                onChange={(e) => set('duration', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
              >
                {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Max Students</label>
              <input
                type="number"
                value={form.maxStudents}
                onChange={(e) => set('maxStudents', parseInt(e.target.value, 10) || 1)}
                min={1}
                max={200}
                className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.maxStudents ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
              />
              {errors.maxStudents && <p className="text-xs text-red-500 mt-1">{errors.maxStudents}</p>}
            </div>
          </div>

          {/* Level & Board */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Level</label>
              <div className="flex gap-2">
                {LEVELS.map((l) => (
                  <button
                    key={l}
                    onClick={() => set('level', l)}
                    className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${form.level === l ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Exam Board</label>
              <select
                value={form.board}
                onChange={(e) => set('board', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
              >
                {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Platform & Meeting Link */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Platform</label>
              <select
                value={form.platform}
                onChange={(e) => set('platform', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
              >
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Meeting Link</label>
              <input
                value={form.meetingLink}
                onChange={(e) => set('meetingLink', e.target.value)}
                placeholder="https://zoom.us/j/..."
                className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.meetingLink ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
              />
              {errors.meetingLink && <p className="text-xs text-red-500 mt-1">{errors.meetingLink}</p>}
            </div>
          </div>

          {/* Status (edit mode only) */}
          {mode === 'edit' && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
              <div className="flex gap-2">
                {['upcoming', 'completed', 'cancelled'].map((s) => (
                  <button
                    key={s}
                    onClick={() => set('status', s)}
                    className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-colors cursor-pointer capitalize whitespace-nowrap ${form.status === s ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Session Notes / Description</label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Add any notes, preparation tips, or topics to cover..."
              rows={3}
              maxLength={500}
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 resize-none"
            />
            <p className="text-xs text-slate-400 text-right mt-1">{form.notes.length}/500</p>
          </div>

          {/* Summary preview */}
          {form.title && form.date && form.time && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">Session Summary</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-600">
                <span><i className="ri-book-open-line mr-1.5 text-emerald-600"></i>{form.title}</span>
                <span><i className="ri-calendar-line mr-1.5 text-emerald-600"></i>{form.date} at {form.time}</span>
                <span><i className="ri-time-line mr-1.5 text-emerald-600"></i>{form.duration}</span>
                <span><i className="ri-group-line mr-1.5 text-emerald-600"></i>Up to {form.maxStudents} students</span>
                <span><i className="ri-award-line mr-1.5 text-emerald-600"></i>{form.level} · {form.board}</span>
                <span><i className="ri-video-line mr-1.5 text-emerald-600"></i>{form.platform}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitted}
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {submitted ? (
              <><i className="ri-check-line"></i> {mode === 'create' ? 'Requested!' : 'Updated!'}</>
            ) : (
              <><i className={mode === 'create' ? 'ri-send-plane-line' : 'ri-save-line'}></i> {mode === 'create' ? 'Submit Request' : 'Save Changes'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
